import { Link, NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { Card, CardBody } from '@/shared/ui/Card';
import { IconChip } from '@/shared/ui/IconChip';
import { Icon, type IconName } from '@/shared/ui/Icon';
import { LogoLockup } from '@/shared/ui/Logo';

/**
 * Moldura da administracao da plataforma.
 *
 * Ela fica fora da navegacao da empresa de proposito. O menu lateral pertence a
 * empresa da sessao, com o que ela contratou e o que o papel permite; nada disso
 * vale aqui. Misturar os dois faria parecer que a administracao da plataforma e
 * mais um modulo da empresa, e ela e o escopo acima de todas.
 *
 * O cabecalho usa a paleta neutra, e nao as cores da empresa da sessao, pelo mesmo
 * motivo.
 *
 * Esconder a moldura de quem nao administra e orientacao, e nao seguranca: o
 * servidor recusa cada rota da plataforma para quem nao tem a concessao.
 */

const secoes: { to: string; label: string; icon: IconName }[] = [
  { to: '/admin/empresas', label: 'Empresas', icon: 'briefcase' },
  { to: '/admin/backups', label: 'Backups', icon: 'layers' },
  { to: '/admin/manutencao', label: 'Manutencao', icon: 'clock' },
  { to: '/admin/trilha', label: 'Trilha da plataforma', icon: 'lock' },
  { to: '/admin/credito/consumo', label: 'Consultas ao bureau', icon: 'coins' },
  // Padrao global dos parametros de credito (C-ADM).
  { to: '/admin/credito/parametros', label: 'Analise de credito', icon: 'chart' },
  // Configuracoes da instalacao, como o E-mail da plataforma (ADR 0096).
  { to: '/admin/configuracoes', label: 'Configuracoes', icon: 'settings' },
];

export function PlatformLayout() {
  const { session } = useAuth();

  if (!session?.platform_admin) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-xl items-center px-4">
        <Card className="w-full">
          <CardBody className="py-12 text-center">
            <IconChip icon="lock" tone="danger" size="lg" className="mx-auto mb-4" />
            <h1 className="text-base font-semibold text-ink-900">Acesso restrito</h1>
            <p className="mt-2 text-sm text-ink-500">
              A administracao da plataforma e reservada a quem recebeu essa concessao.
              Ela nao vem do perfil na empresa.
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex text-sm font-medium text-brand-600 hover:underline"
            >
              Voltar para a empresa
            </Link>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col">
      {/* A barra da plataforma e neutra, e nao da cor da empresa da sessao: ela e da
          SeguraCore. A secao aberta fica em branco sobre o escuro. */}
      <header className="bg-ink-900 bg-linear-to-r from-ink-900 via-ink-800 to-ink-900 text-white">
        <div className="mx-auto flex w-full max-w-[1400px] flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 sm:px-6 lg:px-8">
          <LogoLockup name="SeguraCore" tagline="Administracao da plataforma" />

          <nav aria-label="Administracao da plataforma" className="flex gap-1">
            {secoes.map((secao) => (
              <NavLink
                key={secao.to}
                to={secao.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium',
                    'transition-colors duration-(--duration-fast)',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-400',
                    isActive
                      ? 'bg-white text-ink-900 shadow-(--shadow-sm)'
                      : 'text-white/75 hover:bg-white/10 hover:text-white',
                  )
                }
              >
                <Icon name={secao.icon} size={15} />
                {secao.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-4 text-xs">
            <span className="hidden text-white/60 sm:inline">{session.user.email}</span>
            <Link
              to="/"
              className="flex items-center gap-1.5 rounded-md border border-white/20 px-3 py-1.5 font-medium text-white/85 hover:bg-white/10"
            >
              <Icon name="chevronLeft" size={14} />
              Voltar para {session.tenant.name}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
